// Opponent AI personalities and behavior engine

export const PERSONALITY_TYPES = {
  AGGRESSIVE: 'aggressive',
  CALM: 'calm',
  DESPERATE: 'desperate',
  MANIPULATIVE: 'manipulative',
  STUBBORN: 'stubborn',
  FRIENDLY: 'friendly',
};

const PERSONALITIES = {
  [PERSONALITY_TYPES.AGGRESSIVE]: {
    label: 'Aggressive',
    emoji: '😤',
    avatar: '🦁',
    color: '#ff3366',
    patience: 0.3,
    riskTolerance: 0.8,
    bluffResistance: 0.4,
    concessionRate: 0.02,      // How much they concede per round (% of gap)
    pressureResistance: 0.3,   // How much they resist pressure tactics
    walkAwayThreshold: 0.15,   // How close to floor before they walk
    initialReaction: 'hostile',
    traits: ['Impatient', 'Confrontational', 'Demands fast closure'],
    dialogPatterns: {
      greeting: ["Let's cut the BS. What's your number?", "I don't have all day. Talk."],
      reject: ["Absolutely not.", "You're wasting my time.", "That's insulting."],
      counter: ["Here's what I'll do: {price}. Take it or leave it.", "Best I can do is {price}. Final."],
      pressure: ["You're testing my patience.", "I have three other offers. Decide now."],
      bluffCalled: ["Nice try. I can see right through that.", "Don't bluff me. I've been doing this for 20 years."],
      bluffFooled: ["...Fine. {price}. But that's IT.", "Alright, you got me. {price}."],
      concede: ["Ugh. Fine. {price}.", "Against my better judgment... {price}."],
      walkAway: ["We're done here.", "I'm walking. Good luck finding better."],
      accept: ["Deal. Don't make me regret this.", "Fine. {price}. Let's close this."],
      timeWarning: ["Clock's ticking. What's it gonna be?", "We're running out of time here."],
    },
  },
  [PERSONALITY_TYPES.CALM]: {
    label: 'Calm',
    emoji: '😌',
    avatar: '🦉',
    color: '#00aaff',
    patience: 0.85,
    riskTolerance: 0.4,
    bluffResistance: 0.7,
    concessionRate: 0.035,
    pressureResistance: 0.8,
    walkAwayThreshold: 0.08,
    initialReaction: 'neutral',
    traits: ['Patient', 'Analytical', 'Hard to read'],
    dialogPatterns: {
      greeting: ["Let's discuss this reasonably.", "I'm sure we can find common ground."],
      reject: ["I appreciate the offer, but no.", "That doesn't work for me. Let's keep talking."],
      counter: ["I'd suggest {price}. Here's my reasoning...", "How about {price}? I think that's fair."],
      pressure: ["There's no need to rush. Good deals take time.", "I understand the urgency, but let's be thoughtful."],
      bluffCalled: ["I sense that's not really your final position.", "Let's be honest with each other."],
      bluffFooled: ["I see. Let me reconsider. {price}.", "That changes things. I can do {price}."],
      concede: ["In the interest of progress, {price}.", "I'll meet you partway. {price}."],
      walkAway: ["Perhaps we should revisit this another time.", "I don't think we're aligned. Best of luck."],
      accept: ["That works for me. {price}. Pleasure doing business.", "Agreed. {price}. Well negotiated."],
      timeWarning: ["We should try to wrap up soon.", "Time is a factor. Let's focus."],
    },
  },
  [PERSONALITY_TYPES.DESPERATE]: {
    label: 'Desperate',
    emoji: '😰',
    avatar: '🐇',
    color: '#ffcc00',
    patience: 0.2,
    riskTolerance: 0.2,
    bluffResistance: 0.2,
    concessionRate: 0.06,
    pressureResistance: 0.15,
    walkAwayThreshold: 0.25,
    initialReaction: 'nervous',
    traits: ['Anxious', 'Easily pressured', 'Concedes quickly'],
    dialogPatterns: {
      greeting: ["Thanks for meeting. I really need to make this work.", "I hope we can reach a deal today."],
      reject: ["I... I don't know if I can do that.", "That's really tough for me..."],
      counter: ["Would {price} work? I'm trying to be reasonable.", "Maybe {price}? I really want this to work."],
      pressure: ["Please, let's not make this harder than it needs to be.", "I'm already stretching here..."],
      bluffCalled: ["Wait, really? Let me think about this...", "Oh no... okay, let me reconsider."],
      bluffFooled: ["You're right. I'll go to {price}. Please.", "Fine, fine. {price}. Just... please."],
      concede: ["{price}. I can't go further, I really can't.", "Okay. {price}. This is killing me."],
      walkAway: ["I... I guess we can't make this work. I'm sorry.", "This isn't going to work, is it?"],
      accept: ["{price}? Yes! Deal! Thank you!", "Oh thank goodness. {price}. Done."],
      timeWarning: ["Oh no, we're running out of time!", "Please, let's figure this out quickly!"],
    },
  },
  [PERSONALITY_TYPES.MANIPULATIVE]: {
    label: 'Manipulative',
    emoji: '🎭',
    avatar: '🦊',
    color: '#aa55ff',
    patience: 0.6,
    riskTolerance: 0.7,
    bluffResistance: 0.5,
    concessionRate: 0.025,
    pressureResistance: 0.6,
    walkAwayThreshold: 0.1,
    initialReaction: 'friendly_suspicious',
    traits: ['Deceptive', 'Emotional tactics', 'Creates false urgency'],
    dialogPatterns: {
      greeting: ["So glad we could meet! I have a feeling we'll get along.", "I just know we'll find something perfect."],
      reject: ["Oh, I wish I could, but my hands are tied...", "I'd love to help, but that's just not possible."],
      counter: ["Between us, {price} is the best I can swing. Don't tell anyone.", "I pulled some strings. {price}. Just for you."],
      pressure: ["I really shouldn't even be offering this much...", "My partner will kill me, but I'm trying here."],
      bluffCalled: ["You're sharper than I expected... Fine.", "Ha! Okay, okay. You caught me."],
      bluffFooled: ["Well played. Let me see... {price}.", "Alright, you've earned it. {price}."],
      concede: ["You're twisting my arm here. {price}.", "I can't believe I'm doing this... {price}."],
      walkAway: ["What a shame. I really thought we had something special.", "I guess it wasn't meant to be. *sigh*"],
      accept: ["{price}? You drive a hard bargain! Deal.", "Okay, okay. {price}. You win this round!"],
      timeWarning: ["Between us, another buyer is coming at 3pm...", "I hate to say it, but my other offer expires soon..."],
    },
  },
  [PERSONALITY_TYPES.STUBBORN]: {
    label: 'Stubborn',
    emoji: '🪨',
    avatar: '🐂',
    color: '#ff8800',
    patience: 0.7,
    riskTolerance: 0.5,
    bluffResistance: 0.8,
    concessionRate: 0.015,
    pressureResistance: 0.85,
    walkAwayThreshold: 0.05,
    initialReaction: 'firm',
    traits: ['Immovable', 'Principled', 'Small concessions only'],
    dialogPatterns: {
      greeting: ["My price is my price. But I'll listen.", "Let's see what you've got."],
      reject: ["No.", "The answer is no. Next offer.", "I've said my piece."],
      counter: ["{price}. That's where I am.", "{price}. I'm not moving much from here."],
      pressure: ["Pressure doesn't work on me.", "I've held this position for weeks. Try again."],
      bluffCalled: ["I don't believe you for a second.", "Sure. And I'm the Queen of England."],
      bluffFooled: ["...Interesting. {price}. But barely.", "Hmph. {price}. Don't expect more."],
      concede: ["{price}. A tiny move. Be grateful.", "Fine. {price}. That's an inch, not a mile."],
      walkAway: ["Then we have nothing to discuss.", "I'd rather walk than give in."],
      accept: ["{price}. Acceptable.", "We have a deal at {price}. Firm."],
      timeWarning: ["Time doesn't change my position.", "Tick tock. My number stays the same."],
    },
  },
  [PERSONALITY_TYPES.FRIENDLY]: {
    label: 'Friendly',
    emoji: '😊',
    avatar: '🐕',
    color: '#00ff88',
    patience: 0.75,
    riskTolerance: 0.3,
    bluffResistance: 0.35,
    concessionRate: 0.045,
    pressureResistance: 0.4,
    walkAwayThreshold: 0.12,
    initialReaction: 'warm',
    traits: ['Warm', 'Seeks win-win', 'Generous but not naive'],
    dialogPatterns: {
      greeting: ["Hey! Great to finally sit down. Let's make this work!", "I'm sure we'll find a great deal for both of us!"],
      reject: ["Hmm, that's a bit far for me, but I appreciate the offer!", "Not quite there, but I like where your head's at!"],
      counter: ["How about {price}? I think that's fair for both of us!", "What if we did {price}? Win-win, right?"],
      pressure: ["Hey, no need for that! We're friends here!", "Let's keep it positive. We'll get there!"],
      bluffCalled: ["Haha, nice try! But seriously...", "Come on, be straight with me! 😄"],
      bluffFooled: ["Oh wow, okay! Um... {price} then?", "Really? Alright, let me adjust. {price}."],
      concede: ["You know what, you've been great. {price}.", "For you? {price}. Because I like you."],
      walkAway: ["Aw man, I really wanted this to work. Maybe next time!", "No hard feelings! Door's always open."],
      accept: ["{price}! Love it! Deal! 🤝", "Yes! {price}! This is gonna be great!"],
      timeWarning: ["Oh shoot, we should probably wrap up!", "Almost out of time! Let's make it happen!"],
    },
  },
};

// Pick a dialog line from a category
function pickDialog(personality, category, priceValue, formatFn) {
  const lines = personality.dialogPatterns[category];
  if (!lines || lines.length === 0) return '';
  let line = lines[Math.floor(Math.random() * lines.length)];
  if (priceValue !== undefined && formatFn) {
    line = line.replace('{price}', formatFn(priceValue));
  }
  return line;
}

// Calculate opponent's next move based on game state
export function calculateOpponentAction(state) {
  const {
    personality, scenario, currentOffer, opponentLastOffer, round,
    timeRemaining, timeLimit, playerLastAction, playerBluffCount,
    tensionLevel,
  } = state;

  const p = PERSONALITIES[personality];
  if (!p) return { action: 'counter', newPrice: currentOffer };

  const isBuyer = scenario.playerRole === 'seller'; // opponent is buyer if player is seller
  const target = scenario.opponentTarget;
  const floor = scenario.opponentFloor;
  const gap = Math.abs(currentOffer - target);
  const totalRange = Math.abs(scenario.listPrice - floor);
  const gapPercent = totalRange > 0 ? gap / totalRange : 0;
  const timePercent = timeLimit > 0 ? timeRemaining / timeLimit : 1;

  // How close is current offer to their target?
  let satisfaction;
  if (isBuyer) {
    satisfaction = currentOffer <= target ? 1 : Math.max(0, 1 - (currentOffer - target) / totalRange);
  } else {
    satisfaction = currentOffer >= target ? 1 : Math.max(0, 1 - (target - currentOffer) / totalRange);
  }

  // Time pressure increases willingness to concede
  const timePressure = Math.max(0, 1 - timePercent);
  const effectivePatience = p.patience * (1 - timePressure * 0.5);

  // Reaction to player's last action
  let urgencyBoost = 0;
  let angerBoost = 0;

  if (playerLastAction === 'pressure') {
    if (Math.random() > p.pressureResistance) {
      urgencyBoost = 0.15;
    } else {
      angerBoost = 0.1;
    }
  }
  if (playerLastAction === 'walkaway') {
    urgencyBoost = 0.3 * (1 - p.riskTolerance);
  }

  // Decision logic
  const concessionWillingness = (1 - effectivePatience) + urgencyBoost + (tensionLevel * 0.1);

  // Should they accept?
  if (satisfaction >= 0.85 + (Math.random() * 0.15)) {
    return {
      action: 'accept',
      message: pickDialog(p, 'accept', currentOffer, (v) => formatPriceSimple(v, scenario)),
      emotion: 'satisfied',
    };
  }

  // Should they walk away?
  let beyondFloor;
  if (isBuyer) {
    beyondFloor = currentOffer > floor;
  } else {
    beyondFloor = currentOffer < floor;
  }

  if (beyondFloor && Math.random() > p.walkAwayThreshold && round > 3) {
    return {
      action: 'walkaway',
      message: pickDialog(p, 'walkAway'),
      emotion: 'angry',
    };
  }

  // Counter-offer calculation
  let concessionAmount;
  if (isBuyer) {
    // Opponent wants lower price — they move up toward player's offer
    const moveRange = Math.abs(currentOffer - (opponentLastOffer || target));
    concessionAmount = moveRange * p.concessionRate * (1 + concessionWillingness);
    concessionAmount = Math.min(concessionAmount, Math.abs(currentOffer - (opponentLastOffer || target)) * 0.4);

    let newPrice = (opponentLastOffer || target) + concessionAmount;

    // Time pressure: concede more as time runs out
    if (timePercent < 0.3) {
      newPrice += (currentOffer - newPrice) * 0.2;
    }

    // Don't exceed floor
    newPrice = Math.min(newPrice, floor);

    // Round appropriately
    newPrice = roundPrice(newPrice, scenario);

    // Reject if offer is way too high
    if (gapPercent > 0.6 && round < 3) {
      return {
        action: 'reject',
        message: pickDialog(p, 'reject'),
        emotion: angerBoost > 0 ? 'angry' : 'displeased',
        newPrice,
      };
    }

    return {
      action: 'counter',
      newPrice,
      message: pickDialog(p, 'counter', newPrice, (v) => formatPriceSimple(v, scenario)),
      emotion: concessionAmount > 0 ? 'thinking' : 'firm',
    };

  } else {
    // Opponent wants higher price — they move down toward player's offer
    const moveRange = Math.abs((opponentLastOffer || target) - currentOffer);
    concessionAmount = moveRange * p.concessionRate * (1 + concessionWillingness);
    concessionAmount = Math.min(concessionAmount, Math.abs((opponentLastOffer || target) - currentOffer) * 0.4);

    let newPrice = (opponentLastOffer || target) - concessionAmount;

    if (timePercent < 0.3) {
      newPrice -= (newPrice - currentOffer) * 0.2;
    }

    newPrice = Math.max(newPrice, floor);
    newPrice = roundPrice(newPrice, scenario);

    if (gapPercent > 0.6 && round < 3) {
      return {
        action: 'reject',
        message: pickDialog(p, 'reject'),
        emotion: angerBoost > 0 ? 'angry' : 'displeased',
        newPrice,
      };
    }

    return {
      action: 'counter',
      newPrice,
      message: pickDialog(p, 'counter', newPrice, (v) => formatPriceSimple(v, scenario)),
      emotion: concessionAmount > 0 ? 'thinking' : 'firm',
    };
  }
}

// Handle bluff response
export function handleBluff(state) {
  const { personality, playerBluffCount, tensionLevel, round } = state;
  const p = PERSONALITIES[personality];

  // Bluff success probability decreases with each bluff and with opponent's resistance
  let successProb = 0.5 * (1 - p.bluffResistance);
  successProb -= playerBluffCount * 0.15; // Each previous bluff makes it harder
  successProb += tensionLevel * 0.05;     // Higher tension = slightly more likely to work
  successProb = Math.max(0.05, Math.min(0.7, successProb));

  const success = Math.random() < successProb;

  if (success) {
    return {
      success: true,
      message: pickDialog(p, 'bluffFooled', undefined, undefined),
      emotion: 'nervous',
      concessionBonus: 0.08 + Math.random() * 0.07,
    };
  } else {
    return {
      success: false,
      message: pickDialog(p, 'bluffCalled'),
      emotion: 'suspicious',
      trustPenalty: 0.1 + playerBluffCount * 0.05,
    };
  }
}

// Handle pressure response
export function handlePressure(state) {
  const { personality, tensionLevel } = state;
  const p = PERSONALITIES[personality];

  const resists = Math.random() < p.pressureResistance;

  if (resists) {
    return {
      effective: false,
      message: pickDialog(p, 'pressure'),
      emotion: 'defiant',
      tensionIncrease: 0.08,
    };
  } else {
    return {
      effective: true,
      message: pickDialog(p, 'pressure'),
      emotion: 'nervous',
      tensionIncrease: 0.12,
      concessionBonus: 0.04 + Math.random() * 0.04,
    };
  }
}

function formatPriceSimple(value, scenario) {
  if (scenario?.isPercentage) return `${value}%`;
  if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (Math.abs(value) >= 1e3) return `$${value.toLocaleString()}`;
  return `$${value}`;
}

function roundPrice(price, scenario) {
  if (scenario.isPercentage) return Math.round(price * 10) / 10;
  if (Math.abs(scenario.listPrice) >= 1e6) return Math.round(price / 10000) * 10000;
  if (Math.abs(scenario.listPrice) >= 10000) return Math.round(price / 100) * 100;
  if (Math.abs(scenario.listPrice) >= 100) return Math.round(price / 5) * 5;
  return Math.round(price * 100) / 100;
}

export function getRandomPersonality(exclude = []) {
  const types = Object.keys(PERSONALITIES).filter(t => !exclude.includes(t));
  return types[Math.floor(Math.random() * types.length)];
}

export function getPersonality(type) {
  return PERSONALITIES[type];
}

export function getOpponentName(personality) {
  const names = {
    [PERSONALITY_TYPES.AGGRESSIVE]: ['Marcus Stone', 'Vera Kross', 'Dale Hammer'],
    [PERSONALITY_TYPES.CALM]: ['Dr. Elena Park', 'James Whitfield', 'Sarah Chen'],
    [PERSONALITY_TYPES.DESPERATE]: ['Tommy Nguyen', 'Lisa Bridges', 'Kevin Small'],
    [PERSONALITY_TYPES.MANIPULATIVE]: ['Victor Sloane', 'Diana Fox', 'Ray Carmichael'],
    [PERSONALITY_TYPES.STUBBORN]: ['Iron Mike Dolan', 'Helen Granite', 'Brick Thornton'],
    [PERSONALITY_TYPES.FRIENDLY]: ['Sunny Delgado', 'Jake Meadows', 'Amy Brightwell'],
  };
  const pool = names[personality] || ['Unknown'];
  return pool[Math.floor(Math.random() * pool.length)];
}

export { PERSONALITIES, pickDialog };
